import { InMemoryCache } from '@apollo/client/cache';
import { offsetLimitPagination } from '@apollo/client/utilities';
import { Order } from 'models/order';
import { ScannerCase } from 'models/scanner-case';
import { deepMergeField, replaceFromOffsetZero, shallowMergeKeepingNull } from './merge-policies';

export const cache: InMemoryCache = new InMemoryCache({
    typePolicies: {
        User: {
            fields: {
                systemSetting: {
                    merge(existing = {}, incoming) {
                        return Object.assign({}, JSON.parse(JSON.stringify(existing)), incoming);
                    },
                },
            },
        },
        Dentist: {
            keyFields: false,
        },
        Organization: {
            fields: {
                invoicing: {
                    merge(existing = {}, incoming) {
                        return Object.assign({}, JSON.parse(JSON.stringify(existing)), incoming || {});
                    },
                },
            },
        },
        Order: {
            fields: {
                deliveryDetails: shallowMergeKeepingNull,
                patient: shallowMergeKeepingNull,
                created: deepMergeField,
                archived: deepMergeField,
                productionPlan: deepMergeField,
                linkedDentists: deepMergeField,
                linkedExtTechs: deepMergeField,
                restorations: {
                    merge(_, incoming) {
                        return incoming;
                    },
                },
            },
        },
        OrderWorkFlow: {
            fields: {
                currentStep: deepMergeField,
            },
        },
        WorkFlow: {
            fields: {
                steps: {
                    merge(_, incoming) {
                        return incoming;
                    },
                },
            },
        },
        Material: {
            keyFields: false,
        },
        // The original-order snapshot reuses the live order's restoration and row ids, so normalising
        // them by _id merged the clinic's original rows into the lab's saved ones (DD-3658).
        Restoration: {
            keyFields: false,
        },
        RestorationAction: {
            keyFields: false,
        },
        RestorationMaterial: {
            keyFields: false,
        },
        MaterialPriceList: {
            fields: {
                materials: {
                    merge(_, incoming) {
                        return incoming;
                    },
                },
            },
        },
        Query: {
            fields: {
                dentists: offsetLimitPagination(['clinicId', 'filters']),
                users: offsetLimitPagination(['orgId', 'filters']),
                technicians: offsetLimitPagination(['labId', 'filters']),
                parentClinics: {
                    keyArgs: ['filters', 'orderBy', 'orderDirection'],
                    merge(existing = [], incoming, { args }) {
                        if (args?.offset === 0) {
                            return incoming;
                        }
                        return [...existing, ...incoming];
                    },
                },
                ptjClinics: offsetLimitPagination(['filters', 'orderBy', 'orderDirection']),
                childClinics: {
                    keyArgs: ['orgId', 'filters', 'orderBy', 'orderDirection'],
                    merge(existing = [], incoming, { args }) {
                        if (args?.offset === 0) {
                            return incoming;
                        }
                        return [...existing, ...incoming];
                    },
                },
                labsForAdmin: offsetLimitPagination(['filters', 'orderBy', 'orderDirection']),
                ordersForDentist: replaceFromOffsetZero(['filters']),
                ordersForTechnician: {
                    keyArgs: ['filters', 'orderBy', 'orderDirection', 'currentLabId'],
                    merge(existing = [], incoming, { args }) {
                        if (args?.offset === 0) {
                            return incoming;
                        }
                        return [...existing, ...incoming];
                    },
                },
                supplierOrders: replaceFromOffsetZero(['filters']),
                // DD-3662 supplier browser: a page object ({ total, labs }), so "Show more" appends labs.
                supplierDirectory: {
                    keyArgs: ['search', 'services', 'sort', 'maxDistanceKm', 'hideConnected', 'onlyLocated'],
                    merge(existing, incoming, { args }) {
                        if (!existing || !args?.offset) return incoming;
                        return { ...incoming, labs: [...existing.labs, ...incoming.labs] };
                    },
                },
                // DD-3698 Find customer: the same page object, per country and customer types.
                customerDirectory: {
                    keyArgs: ['search', 'country', 'customerTypes', 'sort', 'maxDistanceKm', 'onlyLocated'],
                    merge(existing, incoming, { args }) {
                        if (!existing || !args?.offset) return incoming;
                        return { ...incoming, labs: [...existing.labs, ...incoming.labs] };
                    },
                },
                // DD-3696 Find lab: the same page object for clinics.
                labDirectory: {
                    keyArgs: ['search', 'services', 'sort', 'maxDistanceKm', 'hideConnected', 'onlyLocated'],
                    merge(existing, incoming, { args }) {
                        if (!existing || !args?.offset) return incoming;
                        return { ...incoming, labs: [...existing.labs, ...incoming.labs] };
                    },
                },
                // DD-3687 historical archive: a page object ({ rows, hasMore }), so
                // infinite scroll appends rows. The backend sorts by
                // { orderDate, _id } precisely so these pages do not overlap.
                historicalOrders: {
                    keyArgs: ['value', 'dateFrom', 'dateTo'],
                    merge(existing, incoming, { args }) {
                        if (!existing || !args?.offset) return incoming;
                        return { ...incoming, rows: [...existing.rows, ...incoming.rows] };
                    },
                },
                draftOrders: offsetLimitPagination(['filters']),
                invoices: {
                    keyArgs: ['filters', 'orderBy', 'orderDirection'],
                    merge(existing = [], incoming, { args }) {
                        if (args?.offset === 0) {
                            return incoming;
                        }
                        return [...(existing || []), ...incoming];
                    },
                },
                invoicesForDentist: {
                    keyArgs: ['filters', 'orderBy', 'orderDirection'],
                    merge(existing = [], incoming, { args }) {
                        if (args?.offset === 0) {
                            return incoming;
                        }
                        return [...(existing || []), ...incoming];
                    },
                },
                reviewOrders: {
                    keyArgs: ['filters', 'orderBy', 'orderDirection'],
                    merge(existing = {}, incoming, { args, readField, mergeObjects }) {
                        if (!Number.isInteger(args?.offset)) {
                            throw new Error('Args is missing from request, Something is wrong');
                        }
                        const mergedOrders = existing.orders ? existing.orders.slice(0) : [];
                        const existingIdSet = new Set(mergedOrders.map((order: Order) => readField('_id', order)));
                        const nonExistingOrders = (incoming.orders || []).filter(
                            (order: Order) => !existingIdSet.has(readField('_id', order)),
                        );
                        if (args?.offset === 0) {
                            return {
                                orderCount: mergeObjects(existing.orderCount, incoming.orderCount),
                                orders: [...nonExistingOrders, ...(existing.orders || [])],
                            };
                        } else {
                            return {
                                orderCount: mergeObjects(existing.orderCount, incoming.orderCount),
                                orders: [...(existing.orders || []), ...incoming.orders],
                            };
                        }
                    },
                },
                attachCaseSearch: {
                    keyArgs: ['orderId', 'name', 'pin'],
                    merge(existing = {}, incoming, { args, readField, mergeObjects }) {
                        if (!Number.isInteger(args?.offset)) {
                            throw new Error('Args is missing from request, Something is wrong');
                        }
                        const mergedScannerCases = existing.scannerCases ? existing.scannerCases.slice(0) : [];
                        const existingIdSet = new Set(
                            mergedScannerCases.map((scannerCase: ScannerCase) => readField('_id', scannerCase)),
                        );
                        const nonExistingScannerCases = (incoming.scannerCases || []).filter(
                            (scannerCase: ScannerCase) => !existingIdSet.has(readField('_id', scannerCase)),
                        );
                        if (args?.offset === 0) {
                            return {
                                listType: mergeObjects(existing.listType, incoming.listType),
                                scannerCases: [...nonExistingScannerCases, ...(existing.scannerCases || [])],
                            };
                        } else {
                            return {
                                listType: mergeObjects(existing.listType, incoming.listType),
                                scannerCases: [...(existing.scannerCases || []), ...incoming.scannerCases],
                            };
                        }
                    },
                },
                stepOrders: {
                    keyArgs: ['filters', 'step'], // keep cache separate per filter/step
                    merge(existing = [], incoming, { args }) {
                        // If offset = 0, it's a new query (sort/filter changed) → replace
                        if (args?.offset === 0) {
                            return incoming;
                        }
                        // Otherwise append for pagination
                        return [...existing, ...incoming];
                    },
                },
                orderListItems: {
                    keyArgs: ['filters', 'step'],
                    merge(existing = [], incoming, { args }) {
                        if (args?.offset === 0) {
                            return incoming;
                        }
                        return [...existing, ...incoming];
                    },
                },
                labClients: {
                    keyArgs: ['filters'],
                    merge(existing = [], incoming, { args }) {
                        if (!args || args.offset === 0) {
                            // New query, reset list
                            return incoming;
                        }
                        // Append for pagination
                        return [...existing, ...incoming];
                    },
                },
                suppliers: {
                    keyArgs: ['filters'],
                    merge(existing = [], incoming, { args }) {
                        if (!args || args.offset === 0) {
                            // New query, reset list
                            return incoming;
                        }
                        // Append for pagination
                        return [...existing, ...incoming];
                    },
                },
                patientOrdersList: offsetLimitPagination(['searchText']),
                uncertifiedOrders: offsetLimitPagination(['filters']),
                invoiceNotes: offsetLimitPagination(['filters']),
                notifications: offsetLimitPagination(['onlyUnread', 'onlyNotDone', 'types', 'category']),
                labMaterials: offsetLimitPagination(['filters', 'orderBy', 'orderDirection']),
                materialPriceLists: offsetLimitPagination(['filters']),
                labActions: offsetLimitPagination(['filters', 'orderBy', 'orderDirection']),
                categories: offsetLimitPagination(['filters']),
                actionPriceLists: offsetLimitPagination(['filters']),
                scanCases: offsetLimitPagination(['pin', 'filters', 'orderDirection']),
                deliveryScheduleNoDeadlineOrders: offsetLimitPagination(['isExternal', 'sortBy', 'sortDirection']),
                deliveryScheduleLateOrders: offsetLimitPagination(['isExternal', 'sortBy', 'sortDirection']),
                millingBatches: offsetLimitPagination(['filters']),
                relatedClinics: offsetLimitPagination(['clinicId']),
                orderForms: offsetLimitPagination(['status', 'search']),
                labsForClinic: offsetLimitPagination(['notConnected', 'searchText']),
            },
        },
        WorkflowStep: {
            keyFields: ['stepId'],
        },
    },
});
